/**
 * UX-1: App-wide QueryClient factory.
 *
 * Centralises QueryClient construction so tests can create an isolated instance
 * with the same MutationCache.onError behaviour as the real app.
 *
 * MutationCache.onError fires a sonner toast.error for every failing mutation.
 * React Query runs BOTH the cache-level handler AND any per-mutation onError,
 * so this global handler does not suppress component-level error handling.
 */

import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractMutationErrorMessage } from '../lib/mutationErrorToast';

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Retry once, but never retry auth/permission errors — they won't
        // resolve on retry.
        retry: (failureCount, error) => {
          const msg = (error as Error)?.message ?? '';
          if (msg.includes('401') || msg.includes('403')) return false;
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
        // PERF-4: global staleTime floor (30s). Without this, queries default
        // to staleTime 0 → refetch on every mount/observer. Components that
        // need fresher or longer cache windows still override per-query.
        staleTime: 30_000,
      },
    },
    mutationCache: new MutationCache({
      onError(error) {
        toast.error(extractMutationErrorMessage(error));
      },
    }),
  });
}
